import * as React from 'react';
import { getGetJourneyRecordQueryKey, JourneyKey, useGetJourneyRecord, type Enquiry } from '@cdf/api-client-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { enquiryStatusLabels } from './enquiry-meta';
import { RecordLink } from '@/components/interaction/interaction-scope';
import { QueryFeedback } from '@/components/interaction/query-feedback';

/** Read-only projection. The native journey supplies requirements and transition eligibility. */
export function EnquiryOpportunityJourney({ enquiry }: { enquiry: Enquiry }) {
  const heading = React.useId();
  const process = useGetJourneyRecord(JourneyKey.enquiry_to_outcome, enquiry.id, { query: { queryKey: getGetJourneyRecordQueryKey(JourneyKey.enquiry_to_outcome, enquiry.id), retry: false } });
  if (process.isError) return <QueryFeedback state="error" title="Enquiry progress is unavailable." description="Reload current progress before continuing qualification." onRetry={() => { void process.refetch(); }} />;
  if (!process.data || process.isLoading) return <QueryFeedback state="loading" title="Loading enquiry progress…" />;
  const state = process.data;
  const conversion = state.validTransitions.find(option => option.stageId === 'converted');
  const current = state.definition.stages.find(stage => stage.id === state.currentStageId);
  const consistent = state.updatedAt === enquiry.updatedAt && state.currentStageId === enquiry.status;
  const converted = enquiry.status === 'converted';
  return <section aria-labelledby={heading} className="min-w-0 space-y-3 rounded-lg border bg-card p-4 text-sm">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <h2 id={heading} className="font-semibold">Enquiry to opportunity</h2>
      <Badge variant="outline">{enquiryStatusLabels[enquiry.status]}</Badge>
    </div>
    <p className="text-muted-foreground">Progress comes from this enquiry and its current native process. Opening a step does not complete it.</p>
    <ol aria-label="Enquiry to opportunity progress" className="grid gap-3 sm:grid-cols-2">
      <li className="min-w-0 rounded-md border p-3">
        <h3 className="font-medium">1. Read the source</h3>
        <p className="break-words">{enquiry.subject}</p>
        <p className="break-words text-muted-foreground">{enquiry.name} · {enquiry.email}</p>
        <p className="mt-1 whitespace-pre-wrap break-words">{enquiry.message}</p>
      </li>
      <li className="rounded-md border p-3" aria-current={!converted ? 'step' : undefined}>
        <h3 className="font-medium">2. Qualify the enquiry</h3>
        <p>{current?.label ?? state.currentStageId}</p>
        <p className="text-muted-foreground">{current?.objective}</p>
        <ul className="mt-1 space-y-1">{state.requirements.map(item => <li key={item.id}>
          <span className="font-medium">{item.satisfied ? 'Complete' : 'Required'}: {item.label}</span>
          <span className="block text-muted-foreground">{item.detail}</span>
        </li>)}</ul>
      </li>
      <li className="rounded-md border p-3">
        <h3 className="font-medium">3. Review contact and opportunity</h3>
        <p>{converted ? 'Conversion completed.' : !consistent ? 'Source and process changed. Reload the enquiry before reviewing conversion.' : conversion && !conversion.blocked
          ? 'Conversion is available. Choose whether to open an opportunity, correct its title and value, then review the exact request on the enquiry screen.'
          : 'Complete the native qualification requirements before conversion.'}</p>
        {!converted && conversion?.missingRequirements.length ? <ul className="mt-1 list-inside list-disc">{conversion.missingRequirements.map(item => <li key={item}>{item}</li>)}</ul> : null}
        <p className="mt-1 text-muted-foreground">The native service matches or creates the contact and any account. An opportunity is optional and starts in the configured pipeline.</p>
      </li>
      <li className="rounded-md border p-3" aria-current={converted ? 'step' : undefined}>
        <h3 className="font-medium">4. Continue the exact result</h3>
        <div className="mt-1 flex flex-col items-start gap-2">
          {enquiry.contactId && <RecordLink className="underline" to={'/partner-engagement/contacts/' + enquiry.contactId}>View linked contact</RecordLink>}
          {enquiry.accountId && <RecordLink className="underline" to={'/partner-engagement/accounts/' + enquiry.accountId}>View linked account</RecordLink>}
          {enquiry.opportunityId ? <RecordLink className="underline" to={'/partner-engagement/opportunities/' + enquiry.opportunityId}>View opened opportunity</RecordLink>
            : <p className="text-muted-foreground">{converted ? 'No opportunity was created by this conversion.' : 'No linked opportunity yet.'}</p>}
        </div>
      </li>
    </ol>
    {!consistent && <Button variant="outline" size="sm" onClick={() => { void process.refetch(); }}>Reload process</Button>}
  </section>;
}
